import { Box, Stack, useTheme } from "@mui/material";
import { LargeContentBox } from "@/components/common";
import { ClinicalInsightsFilterBar } from "src-next/modules/clinical-insights/components/page/ClinicalInsightsFilterBar";
import {
  AnalysisDetailFrame,
  InsightFrameGuide,
  WORKSPACE_ANALYSES,
  getAnalysisAccentColor,
  getInsightAnalysisTexts,
} from "src-next/modules/clinical-insights/components/workspace";
import { AnalysisCardRows } from "./AnalysisCardRows";
import { useAnalysisCardGroups } from "./useAnalysisCardGroups";
import { useAnalysisComponents } from "./buildAnalysisComponents";
import { useClinicalInsightsPageState } from "./useClinicalInsightsPageState";

export interface ClinicalInsightsPageProps {
  organizationId?: string;
  locationActors?: string[];
  accessType?: string;
}

export default function ClinicalInsightsPage(_props: ClinicalInsightsPageProps) {
  const theme = useTheme();
  const page = useClinicalInsightsPageState();
  const { treatmentGroupCards, diagnosticsGroupCards, consentGroupCards, cardTitleById } =
    useAnalysisCardGroups(theme);

  const analysisComponents = useAnalysisComponents({
    theme: page.theme,
    selectedAnalysisId: page.selectedAnalysisId,
    visitedAnalysisIds: page.visitedAnalysisIds,
    loadPhaseCallbacks: page.loadPhaseCallbacks,
    sharedInsightProps: page.sharedInsightProps,
    workspaceInsightProps: page.workspaceInsightProps,
  });

  return (
    <LargeContentBox>
      <Stack width="100%" spacing={1.5}>
        {page.showPractitionerInTopBar ? (
          <ClinicalInsightsFilterBar
            practitionerId={page.filters.practitionerId}
            onPractitionerChange={page.filters.handlePractitionerChange}
            practitioner={page.filters.practitioner}
            showPractitionerSelect
          />
        ) : null}

        <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0 }}>
          <AnalysisCardRows
            treatmentCards={treatmentGroupCards}
            diagnosticsCards={diagnosticsGroupCards}
            consentCards={consentGroupCards}
            selectedAnalysisId={page.selectedAnalysisId}
            isBusy={page.isBusy}
            onSelect={page.setSelectedAnalysisId}
          />
        </Stack>

        <Box width="100%">
          {Array.from(page.visitedAnalysisIds).map((id) => (
            <Box
              key={id}
              sx={{
                display: id === page.selectedAnalysisId ? "block" : "none",
                width: "100%",
                scrollMarginTop: 8,
              }}
            >
              <AnalysisDetailFrame
                ref={id === page.selectedAnalysisId ? page.setFrameRef : undefined}
                accentColor={
                  id === page.selectedAnalysisId
                    ? page.selectedAccentColor
                    : getAnalysisAccentColor(theme, id)
                }
                analysisName={cardTitleById.get(id) ?? id}
                filteredPatientCount={
                  id === page.selectedAnalysisId ? page.patientStats.filtered : 0
                }
                totalPatientCount={
                  id === page.selectedAnalysisId ? page.patientStats.total : 0
                }
                scrollParentContentHeight={
                  id === page.selectedAnalysisId ? page.scrollParentContentHeight : 0
                }
                isBusy={id === page.selectedAnalysisId && page.isBusy}
                busyMetaText={
                  id === page.selectedAnalysisId ? page.busyMetaText : undefined
                }
                actionGuide={
                  WORKSPACE_ANALYSES.has(id) || id === "sem-atendimento" ? (
                    <InsightFrameGuide text={getInsightAnalysisTexts(id).actionGuideText} />
                  ) : undefined
                }
                periodControl={
                  id === page.selectedAnalysisId && id !== "sem-atendimento"
                    ? page.framePeriodControl
                    : undefined
                }
              >
                {analysisComponents[id]}
              </AnalysisDetailFrame>
            </Box>
          ))}
        </Box>
      </Stack>
    </LargeContentBox>
  );
}

/** @deprecated Use default export ClinicalInsightsPage */
export function ClinicalAnalysis() {
  return <ClinicalInsightsPage />;
}
